import { useCallback, useEffect, useState, useRef, type ChangeEvent, type MouseEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { Page } from "@boundary/layouts/pageLayout";
import { Button } from "@boundary/components/button";
import { Spinner } from "@boundary/components/spinner";
import { useOrderFormState } from "@boundary/_hooks/useOrderFormState";
import { useCollapsible } from "@boundary/_hooks/useCollapsible";
import { useFunnelStep } from "@boundary/_hooks/useFunnelStep";
import { getInfoByService } from "@domain/enums/local/getInfoByService";
import { ServiceType } from "@domain/enums/machine.enums";
import { formatRubles } from "@shared/utils/number/formatRubles";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FileListItem } from "@boundary/widgets/fileListItem";
import { isValidRange } from "@boundary/utils/pageRange";
import { ColorSelect } from "@boundary/widgets/colorSelect";
import { PagesSelect } from "@boundary/widgets/pagesSelect";
import { FormatSelect } from "@boundary/widgets/formatSelect";
import { DuplexSelect } from "@boundary/widgets/duplexSelect";
import { ResourcesInsufficientModal } from "@boundary/components/ResourcesInsufficientModal";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import { Dialog, DialogTrigger } from "@boundary/components/dialog";
import { IconDialog } from "@boundary/widgets/iconDialog";
import { InfoIcon } from "@boundary/components/icons";
import { PhotoEditorOverlay } from "@boundary/widgets/photoEditorOverlay";
import styles from "./service.module.scss";

const IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "image/png",
  "image/jpeg",
  "image/webp"
];

const INITIALIZATION_TIMEOUT = 100;

export const ServicePage = observer(function ServicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const machineIdParam = searchParams.get("machineId");
  const serviceTypeParam = searchParams.get("serviceType");
  const fromParentParam = searchParams.get("fromParent");
  const machineId = machineIdParam ? Number(machineIdParam) : 0;
  const serviceType = serviceTypeParam as ServiceType | null;

  useFunnelStep("service_selected");

  const vm = useOrderFormState(machineId);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [editorImageUrl, setEditorImageUrl] = useState<string | undefined>();
  const ref = useCollapsible(expanded);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!serviceType) return;

    const existingServiceInCart = vm.services.find(s => s.type === serviceType);
    
    if (!vm.currentService || vm.currentService.type !== serviceType) {
      if (existingServiceInCart) {
        vm.editServiceFromCart(existingServiceInCart.id);
      } else {
        vm.addService(serviceType);
      }
    } else if (vm.currentService.files.length > 0 && !vm.currentFile) {
      runInAction(() => {
        vm.currentFile = vm.currentService!.files[0];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  useEffect(() => {
    if (!serviceType) {
      prefetchAndNavigate(navigate, "/catalog");
      return;
    }

    const timer = setTimeout(() => {
      if (!vm.currentService || vm.currentService.type !== serviceType) {
        prefetchAndNavigate(navigate, "/catalog");
      }
    }, INITIALIZATION_TIMEOUT);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType, navigate]);

  const handleFileUpload = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return;

      const allFiles = Array.from(e.target.files);
      const selectedFiles = allFiles.filter((f) => ALLOWED_FILE_TYPES.includes(f.type));

      if (selectedFiles.length === 0) return;

      setLoading(true);
      
      void vm.addFiles(selectedFiles)
        .then(() => {
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        })
        .catch(() => {
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [vm]
  );

  const handleBack = useCallback((e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const existingServiceInCart = vm.services.find(s => s.type === serviceType);

    if (existingServiceInCart) {
      prefetchAndNavigate(navigate, `/cart?machineId=${machineId}`);
    } else {
      const catalogUrl = fromParentParam
        ? `/catalog?machineId=${machineId}&parent=${encodeURIComponent(fromParentParam)}`
        : `/catalog?machineId=${machineId}`;
      prefetchAndNavigate(navigate, catalogUrl);
    }
  }, [navigate, vm.services, serviceType, machineId, fromParentParam]);

  const handleInspectFile = useCallback((fileId: string) => {
    const file = vm.currentService?.files.find(f => f.id === fileId);
    if (!file) return;

    runInAction(() => {
      vm.currentFile = file;
    });

    const isImage = /\.(png|jpe?g|webp)$/i.test(file.name) && file.originalUrl;
    if (isImage) {
      setEditorImageUrl(file.originalUrl);
      setShowPhotoEditor(true);
    } else {
      prefetchAndNavigate(navigate, `/docview?machineId=${machineId}&fileId=${fileId}`);
    }
  }, [vm, machineId, navigate]);

  const handleAddToCart = useCallback(() => {
    if (!vm.currentService || vm.currentService.files.length === 0) return;

    // Проверяем достаточность ресурсов перед добавлением в корзину
    if (vm.priceCalculation && !vm.priceCalculation.resourcesSufficient) {
      setShowResourcesModal(true);
      return;
    }

    vm.addServiceToCart();
    prefetchAndNavigate(navigate, `/cart?machineId=${machineId}`);
  }, [vm, machineId, navigate]);

  // Рассчитываем базовую цену из файлов (без промокода)
  // MobX observer автоматически отслеживает изменения в vm.filePrice
  const filesPrice = vm.currentService?.files.reduce(
    (acc, file) => acc + (vm.filePrice[file.id] ?? 0),
    0
  ) ?? 0;
  
  const hasFilesWithPrice = (vm.currentService?.files.length ?? 0) > 0 && filesPrice > 0;
  
  // Показываем попап автоматически при недостаточности ресурсов после расчета цены
  // ВАЖНО: useEffect должен быть до раннего возврата, чтобы соблюдать правила хуков
  useEffect(() => {
    if (vm.priceCalculation && !vm.priceCalculation.resourcesSufficient && hasFilesWithPrice) {
      setShowResourcesModal(true);
    }
  }, [vm.priceCalculation?.resourcesSufficient, hasFilesWithPrice, vm.priceCalculation]);

  if (!serviceType || !vm.currentService) {
    return null;
  }

  const info = getInfoByService(vm.currentService.type);
  
  const resourcesSufficient = vm.priceCalculation?.resourcesSufficient ?? true;
  
  const isFormValid = vm.currentFile
    ? isValidRange(vm.currentFile.pagesRange, vm.currentFile.totalPages) === null
    : true;

  const isServiceInCart = vm.services.includes(vm.currentService);
  
  // Блокируем кнопку если ресурсов недостаточно
  const canAddToCart = isFormValid && resourcesSufficient;

  const infoButton = info.description ? (
    <Dialog modal>
      <DialogTrigger asChild>
        <button className={styles.infoButton}>
          <InfoIcon size={22} />
        </button>
      </DialogTrigger>
      <IconDialog
        iconSrc="/assets/map/printer-bg.png"
        pill={info.price}
        title={info.title}
        description={info.description}
      />
    </Dialog>
  ) : null;

  return (
    <Page>
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header
          className={styles.header}
          back 
          onBackClick={handleBack} 
          center={<h1 className={styles.headerTitle}>{info.title}</h1>}
          right={infoButton}
        />
        <Page.DisplayZone.Body className={styles.content}>
          <div className={styles.filesSection}>
            <h2 className={styles.sectionTitle}>Файлы</h2>
            <form action="contents">
              <label className={styles.fileInputLabel}>
                Добавить файл
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp"
                  multiple
                  className={styles.fileInput}
                  onChange={handleFileUpload}
                />
              </label>
            </form>
            <ul className={styles.filesList}>
              {vm.currentService.files.map((file) => (
                <FileListItem
                  key={file.id}
                  item={file}
                  selected={vm.currentFile === file}
                  onSelect={() => runInAction(() => { vm.currentFile = file; })}
                  onDelete={() => vm.deleteFile(file)}
                  onInspect={() => handleInspectFile(file.id)}
                />
              ))}
              {loading && (
                <div className={styles.spinnerContainer}>
                  <Spinner />
                </div>
              )}
            </ul>
          </div>
          {vm.currentFile && vm.currentFile.id && vm.currentFile.previewUrl && (
            <div className={styles.settingsSection}>
              <PagesSelect vm={vm} />
              <ColorSelect vm={vm} />
              <FormatSelect vm={vm} />
              <DuplexSelect vm={vm} />
            </div>
          )}
        </Page.DisplayZone.Body>
      {hasFilesWithPrice && (
          <Page.DisplayZone.Sticky className={styles.buttonPad}>
          <div className={styles.summaryContainer}>
            <div className={styles.summaryHeader}>
              <span className={styles.summaryLabel}>Сумма</span>
              <p className={styles.summaryPrice}>{formatRubles(filesPrice)}</p>
              {vm.currentService.files.length > 0 && (
                <Button
                  size="icon"
                  variant="secondary"
                  onClick={() => {
                    const newValue = !expanded;
                    console.log(`[ServicePage] Toggling expanded: ${expanded} -> ${newValue}`);
                    setExpanded(newValue);
                  }}
                >
                  {!expanded ? <ChevronDown /> : <ChevronUp />}
                </Button>
              )}
            </div>
            <div ref={ref} className={styles.summaryContent}>
              {vm.currentService.files.map((file, index) => (
                <div className={styles.summaryItem} key={file.id}>
                  <span className={styles.summaryItemLabel}>
                    Документ {index + 1}{file.copiesCount > 1 ? ` x${file.copiesCount}` : ''}
                  </span>
                  <p>{vm.filePrice[file.id] && formatRubles(vm.filePrice[file.id])}</p>
                </div>
              ))}
            </div>
          </div>
          {!resourcesSufficient && (
            <div className={styles.warningMessage}>
              Заказ слишком большой для аппарата в данный момент
            </div>
          )}
          <Button
            onClick={handleAddToCart}
            disabled={!canAddToCart}
          >
            {isServiceInCart ? "В корзину" : "Добавить в корзину"}
          </Button>
          </Page.DisplayZone.Sticky>
      )}
      </Page.DisplayZone>
      
      <ResourcesInsufficientModal
        open={showResourcesModal}
        onOpenChange={setShowResourcesModal}
        missingResources={vm.priceCalculation?.missingResources || []}
        resourcesDetails={vm.priceCalculation?.resourcesDetails || ""}
        machineId={machineId}
      />

      <PhotoEditorOverlay
        open={showPhotoEditor}
        imageUrl={editorImageUrl}
        onClose={() => { setShowPhotoEditor(false); setEditorImageUrl(undefined); }}
      />
    </Page>
  );
});
